import React, { useState, useEffect, useRef } from "react";
import {
  IoVideocamOff as VideoCamOff,
} from 'react-icons/io5';
import {
  IoIosMicOff as MicOffIcon,
} from 'react-icons/io';

const Participant = ({ participant, isLocal = false }) => {
  const videoRef = useRef();
  const audioRef = useRef();
  
  const [videoTrack, setVideoTrack] = useState(null);
  const [audioTrack, setAudioTrack] = useState(null);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);

  // Update tracks whenever participant changes
  useEffect(() => {
    const videoTracks = Array.from(participant.videoTracks.values());
    const audioTracks = Array.from(participant.audioTracks.values());

    // LOCAL PARTICIPANT - keep existing behavior
    if (isLocal) {
      if (videoTracks.length > 0) {
        setVideoTrack(videoTracks[0].track);
      }

      if (audioTracks.length > 0) {
        setAudioTrack(audioTracks[0].track);
      }

      return;
    }

    // -----------------------------------
    // REMOTE PARTICIPANT
    // -----------------------------------

    const cleanupFunctions = [];

    const handleVideoTrack = (track) => {
      if (!track) return;

      setVideoTrack(track);

      // IMPORTANT:
      // Don't treat undefined isEnabled as camera off.
      setVideoEnabled(track.isEnabled !== false);

      const handleDisabled = () => {
        console.log("Remote camera disabled");
        setVideoEnabled(false);
      };

      const handleEnabled = () => {
        console.log("Remote camera enabled");
        setVideoEnabled(true);
      };

      track.on("disabled", handleDisabled);
      track.on("enabled", handleEnabled);

      cleanupFunctions.push(() => {
        track.off("disabled", handleDisabled);
        track.off("enabled", handleEnabled);
      });
    };

    const handleAudioTrack = (track) => {
      if (!track) return;

      setAudioTrack(track);

      // IMPORTANT:
      // Don't treat undefined isEnabled as microphone off.
      setAudioEnabled(track.isEnabled !== false);

      const handleDisabled = () => {
        console.log("Remote microphone disabled");
        setAudioEnabled(false);
      };

      const handleEnabled = () => {
        console.log("Remote microphone enabled");
        setAudioEnabled(true);
      };

      track.on("disabled", handleDisabled);
      track.on("enabled", handleEnabled);

      cleanupFunctions.push(() => {
        track.off("disabled", handleDisabled);
        track.off("enabled", handleEnabled);
      });
    };

    // -----------------------------------
    // EXISTING VIDEO TRACK
    // -----------------------------------

    const screenPublication = videoTracks.find(
      (pub) => pub.track && pub.track.name === "screen"
    );

    const cameraPublication = videoTracks.find(
      (pub) => pub.track && pub.track.name !== "screen"
    );

    const videoPublication = screenPublication || cameraPublication;

    if (videoPublication?.track) {
      handleVideoTrack(videoPublication.track);
    }

    // -----------------------------------
    // EXISTING AUDIO TRACK
    // -----------------------------------

    const audioPublication = audioTracks[0];

    if (audioPublication?.track) {
      handleAudioTrack(audioPublication.track);
    }

    // -----------------------------------
    // HANDLE NEWLY SUBSCRIBED TRACKS
    // -----------------------------------

    const handleTrackSubscribed = (track) => {
      if (track.kind === "video") {
        handleVideoTrack(track);
      }

      if (track.kind === "audio") {
        handleAudioTrack(track);
      }
    };

    // -----------------------------------
    // HANDLE UNSUBSCRIBED TRACKS
    // -----------------------------------

    const handleTrackUnsubscribed = (track) => {
      if (track.kind === "video") {
        // Find another active video track.
        // Prefer screen share if another one exists,
        // otherwise fall back to the doctor's camera.
        const remainingVideoTracks = Array.from(
          participant.videoTracks.values()
        ).filter(
          (publication) =>
            publication.track &&
            publication.track !== track
        );

        const screenPublication = remainingVideoTracks.find(
          (publication) => publication.track.name === "screen"
        );

        const cameraPublication = remainingVideoTracks.find(
          (publication) => publication.track.name !== "screen"
        );

        const fallbackPublication =
          screenPublication || cameraPublication;

        if (fallbackPublication?.track) {
          console.log(
            "Switching back to video:",
            fallbackPublication.track.name
          );

          setVideoTrack(fallbackPublication.track);
          setVideoEnabled(
            fallbackPublication.track.isEnabled !== false
          );
        } else {
          // No other video track available
          setVideoTrack(null);
          setVideoEnabled(false);
        }
      }

      if (track.kind === "audio") {
        setAudioTrack(null);
        setAudioEnabled(false);
      }
    };

    participant.on("trackSubscribed", handleTrackSubscribed);
    participant.on("trackUnsubscribed", handleTrackUnsubscribed);

    // Cleanup
    return () => {
      participant.off("trackSubscribed", handleTrackSubscribed);
      participant.off("trackUnsubscribed", handleTrackUnsubscribed);

      cleanupFunctions.forEach((cleanup) => cleanup());
    };
  }, [participant, isLocal]);

  // Attach video track
  useEffect(() => {
    if (videoTrack && videoRef.current) {
      videoTrack.attach(videoRef.current);
      return () => {
        videoTrack.detach();
      };
    }
  }, [videoTrack]);

  // Attach audio track
  useEffect(() => {
    if (audioTrack && audioRef.current) {
      audioTrack.attach(audioRef.current);
      return () => {
        audioTrack.detach();
      };
    }
  }, [audioTrack]);

  // Get status message only for remote participants
  const getStatusMessage = () => {
    if (isLocal) return null;

    const videoOff = !videoEnabled;
    const audioOff = !audioEnabled;

    if (videoOff && audioOff) {
      return (
        <div className="flex flex-col items-center gap-2 text-[#111827] bg-white/50 backdrop-blur-sm px-3 py-1.5 rounded-md border border-white/10">
          <div className="flex flex-row text-lg gap-x-1">
            <VideoCamOff />
            <MicOffIcon />
          </div>
          <span>Camera and Microphone are off</span>
        </div>
      );
    }
    if (videoOff) {
      return (
        <div className="flex flex-col items-center gap-2 text-[#111827] bg-white/50 backdrop-blur-sm px-3 py-1.5 rounded-md border border-white/10">
          <div className="flex flex-row text-lg">
            <VideoCamOff />
          </div>
          <span>Camera is off</span>
        </div>
      );
    }
    if (audioOff) {
      return (
        <div className="flex flex-col items-center gap-2 text-[#111827] bg-white/50 backdrop-blur-sm px-3 py-1.5 rounded-md border border-white/10">
          <div className="flex flex-row text-lg">
            <MicOffIcon />
          </div>
          <span>Microphone is off</span>
        </div>
      );
    }
    return null;
  };

  const statusMessage = getStatusMessage();

  return (
    <div className="participant relative w-full h-full bg-black">
      <video
        ref={videoRef}
        autoPlay={true}
        className="w-full h-full object-contain"
      />
      <audio ref={audioRef} autoPlay={true} muted={true} />

      {/* Show status for remote participants only */}
      {statusMessage && (
        <div className="absolute inset-0 flex items-center justify-center text-lg">
          {statusMessage}
        </div>
      )}
    </div>
  );
};

export default Participant;